import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSPersonalInfoData,
  type EnrollmentHYCSPersonalInfoGetInput,
  type EnrollmentHYCSPersonalInfoSubmitInput,
  type EnrollmentHYCSPersonalInfoSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS PersonalInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSPersonalInfoData>
 * @example const response = await getEnrollmentHYCSPersonalInfoData()
 */
export const getEnrollmentHYCSPersonalInfoData = async (
  enrollmentHYCSPersonalInfoGetInput?: EnrollmentHYCSPersonalInfoGetInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSPersonalInfoData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSPersonalInfoData',
      query: `mutation getEnrollmentHYCSPersonalInfoData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHYCSPersonalInfoGetInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSPersonalInfoData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSPersonalInfoData = createQueryHook<
  EnrollmentHYCSPersonalInfoGetInput,
  EnrollmentHYCSPersonalInfoData
>(getEnrollmentHYCSPersonalInfoData)

/**
 * Submit Enrollment HYCS PersonalInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSPersonalInfoSubmitOutput>
 * @example const response = await submitEnrollmentHYCSPersonalInfoData();
 */
export const submitEnrollmentHYCSPersonalInfoData = async (
  enrollmentPersonalInfoSubmitInput: EnrollmentHYCSPersonalInfoSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSPersonalInfoSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSPersonalInfoData',
      query: `mutation submitEnrollmentHYCSPersonalInfoData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentPersonalInfoSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSPersonalInfoSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSPersonalInfoData = createMutationHook<
  EnrollmentHYCSPersonalInfoSubmitInput,
  EnrollmentHYCSPersonalInfoSubmitOutput
>(submitEnrollmentHYCSPersonalInfoData)
